import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, FileDownload as FileDownloadIcon, FileUpload as FileUploadIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import imputableService, { tipoCuentaLabel, normalizeTipoCuentaCodigo } from '../../services/imputableService';
import noImputableService, { formatNoImputableLabel, type NoImputable } from '../../services/noImputableService';
import companiaService from '../../services/companiaService';
import PDFViewer from '../../components/shared/PDFViewer';
import * as XLSX from 'xlsx';

/** Etiqueta de columna "Visible en", alineado al informe legacy (Ambos / Ninguno / IVarios / EVarios). */
function visibleEnReporte(v: string | null | undefined): string {
  const s = v != null ? String(v).trim() : '';
  if (!s) return 'Ninguno';
  if (/^ambos$/i.test(s)) return 'Ambos';
  if (/^ninguno$/i.test(s)) return 'Ninguno';
  if (s === 'IVarios' || /^ivarios$/i.test(s)) return 'IVarios';
  if (s === 'EVarios' || /^evarios$/i.test(s)) return 'EVarios';
  return s;
}

function formatoNombreRubroReporte(ni: NoImputable | undefined, nroRubro: number): string {
  const base = (ni?.nombreCuenta || ni?.descripcioncta || `Rubro ${nroRubro}`).trim();
  return base.toUpperCase();
}

const Imputables: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [imputables, setImputables] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [noImputablePorRubro, setNoImputablePorRubro] = useState<Map<number, NoImputable>>(new Map());

  useEffect(() => {
    let cancelled = false;
    noImputableService
      .getAllNoImputablesPlanCuentas()
      .then((rows) => {
        if (!cancelled) setNoImputablePorRubro(new Map(rows.map((r) => [r.nroRubro, r])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const etiquetaRubroNoImputable = (nroRubro: number | undefined | null) => {
    if (nroRubro == null || nroRubro === 0) return '—';
    const ni = noImputablePorRubro.get(nroRubro);
    return ni ? formatNoImputableLabel(ni) : String(nroRubro);
  };

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = () => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const fetchImputables = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const result = await imputableService.getImputablesPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setImputables(result.items);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      console.error('Error al cargar imputables:', error);
      enqueueSnackbar('Error al cargar imputables', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImputables();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchImputables(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchImputables(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchImputables(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (codImputable: string) => {
    if (window.confirm('¿Está seguro de eliminar este imputable?')) {
      try {
        await imputableService.deleteImputable(codImputable);
        enqueueSnackbar('Imputable eliminado exitosamente', { variant: 'success' });
        fetchImputables();
      } catch (error: any) {
        console.error('Error al eliminar imputable:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar imputable', { variant: 'error' });
      }
    }
  };

  const handleInformePdfPlanCuentas = async () => {
    try {
      setExportingPdf(true);
      const [rowsImp, rowsNoImp, companias] = await Promise.all([
        imputableService.getAllImputablesPlanCuentas(),
        noImputableService.getAllNoImputablesPlanCuentas(),
        companiaService.getCompanias({ pageNumber: 1, pageSize: 100 }).catch(() => [])
      ]);

      if (!rowsImp.length && !rowsNoImp.length) {
        enqueueSnackbar('No hay datos para el informe.', { variant: 'warning' });
        return;
      }

      const empresa = companias[0];
      const empresaNombre = empresa?.nombre?.trim() || '—';
      const empresaLocalidad = empresa?.localidad?.trim() || '—';
      const empresaProvincia = empresa?.provincia?.trim() || '—';

      const noImpMap = new Map<number, NoImputable>(rowsNoImp.map((n) => [n.nroRubro, n]));
      const byRubro = new Map<number, typeof rowsImp>();
      for (const r of rowsImp) {
        const key = r.nroRubro ?? 0;
        const list = byRubro.get(key) ?? [];
        list.push(r);
        byRubro.set(key, list);
      }

      const rubroSet = new Set<number>();
      rowsNoImp.forEach((n) => rubroSet.add(n.nroRubro));
      rowsImp.forEach((r) => rubroSet.add(r.nroRubro ?? 0));
      const rubrosOrdenados = Array.from(rubroSet).sort((a, b) => a - b);

      const rubroBand: [number, number, number] = [236, 245, 255];

      type BodyEl = string | { content: string | number; styles?: Record<string, unknown> };
      type BodyRowKind = 'rubro' | 'cuenta';
      /** Filas del detalle y cortes por rubro (columnas al estilo informe legacy). */
      const body: BodyEl[][] = [];
      /** Para sangrar cuentas respecto al nro de rubro (más a la derecha). */
      const bodyRowKinds: BodyRowKind[] = [];

      for (const rubro of rubrosOrdenados) {
        const ni = noImpMap.get(rubro);
        body.push([
          {
            content: String(rubro),
            styles: {
              fillColor: rubroBand,
              fontStyle: 'bold',
              fontSize: 9,
              halign: 'left' as const,
              cellPadding: 4
            }
          },
          {
            content: formatoNombreRubroReporte(ni, rubro),
            styles: { fillColor: rubroBand, fontStyle: 'bold', fontSize: 9, cellPadding: 4 }
          },
          { content: '', styles: { fillColor: rubroBand, cellPadding: 4 } }
        ]);
        bodyRowKinds.push('rubro');

        const imps = [...(byRubro.get(rubro) ?? [])].sort(
          (a, b) => Number(a.codImputable) - Number(b.codImputable)
        );

        for (const imp of imps) {
          body.push([
            imp.codImputable || '',
            (imp.nombre || '').trim(),
            visibleEnReporte(imp.visibleEn)
          ]);
          bodyRowKinds.push('cuenta');
        }
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 42;
      const marginBottom = 40;
      const headerTop = 38;
      const headerBlockHeight = 72;
      const marginTop = headerTop + headerBlockHeight;

      const today = new Date();
      const fechaStr = today.toLocaleDateString('es-AR');
      const horaStr = today.toLocaleTimeString('es-AR', { hour: 'numeric', minute: '2-digit', hour12: true });

      const metaHeader = () => {
        doc.setTextColor(0);
        doc.setFontSize(15);
        doc.text('Plan De Cuentas', marginX, headerTop);
        doc.setFontSize(10);
        doc.text(`Empresa: ${empresaNombre}`, marginX, headerTop + 20);
        doc.text(`Localidad: ${empresaLocalidad}`, marginX, headerTop + 34);
        doc.text(`Provincia: ${empresaProvincia}`, marginX, headerTop + 48);
      };

      const padBase = { top: 4, bottom: 4, right: 5 } as const;
      const padRubroCol0 = { ...padBase, left: 2 };
      const padCuentaCol0 = { ...padBase, left: 18 };

      autoTable(doc, {
        margin: { left: marginX, right: marginX, top: marginTop, bottom: marginBottom },
        showHead: 'everyPage',
        theme: 'plain',
        tableLineWidth: 0,
        styles: {
          fontSize: 9,
          cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
          valign: 'middle',
          overflow: 'linebreak',
          lineWidth: 0
        },
        headStyles: {
          fillColor: [245, 245, 248],
          textColor: [30, 30, 35],
          fontStyle: 'bold',
          fontSize: 9,
          lineWidth: 0
        },
        columnStyles: {
          0: { cellWidth: 96, halign: 'left' },
          1: { cellWidth: 312 },
          2: { cellWidth: 103, halign: 'left' }
        },
        head: [['Nro Cuenta', 'Descripcion', 'Visible en...']],
        body,
        tableWidth: 'wrap',
        didParseCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 0) return;
          const kind = bodyRowKinds[data.row.index];
          if (kind === 'rubro') {
            data.cell.styles.cellPadding = padRubroCol0;
          } else if (kind === 'cuenta') {
            data.cell.styles.cellPadding = padCuentaCol0;
          }
        },
        willDrawPage: () => {
          metaHeader();
        },
        didDrawPage: (data) => {
          const docRef = data.doc;
          const pageH =
            typeof docRef.internal.pageSize.getHeight === 'function'
              ? docRef.internal.pageSize.getHeight()
              : (docRef.internal.pageSize as unknown as { height: number }).height;
          docRef.setFontSize(8);
          docRef.setTextColor(90);
          docRef.text(`Fecha: ${fechaStr} Hora:${horaStr} Pagina ${data.pageNumber}`, marginX, pageH - marginBottom / 2);
          docRef.setTextColor(0);
        }
      });

      const pdfBlob = doc.output('blob');
      const blob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar PDF del plan de cuentas:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const allItems = await imputableService.getAllImputablesPlanCuentas(searchTerm);
      const dataToExport = allItems.map((item: any) => ({
        'Código': item.codImputable,
        'Nombre': item.nombre,
        'Nro. rubro': item.nroRubro ?? '',
        'No imputable': etiquetaRubroNoImputable(item.nroRubro),
        'Imputable': item.imputable === 'S' ? 'Sí' : 'No',
        'Tipo de cuenta': tipoCuentaLabel(item.tipo),
        'Visible en': visibleEnReporte(item.visibleEn)
      }));
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Imputables');
      XLSX.writeFile(wb, 'Imputables.xlsx');
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        let creados = 0;
        let actualizados = 0;
        let omitidos = 0;

        for (const row of data) {
          const r = row as Record<string, unknown>;
          const nroRubroRaw =
            r['Nro. rubro'] ?? r['NroRubro'] ?? r['Nivel'];
          const nroRubro =
            typeof nroRubroRaw === 'number'
              ? nroRubroRaw
              : parseInt(String(nroRubroRaw ?? '').trim(), 10);
          const tipoRaw = r['Tipo'] ?? r['Tipo de cuenta'];
          const codImputable = String(r['Código'] ?? '').trim();
          if (!codImputable || !Number.isFinite(Number(codImputable))) {
            omitidos += 1;
            continue;
          }
          const imputable = {
            codImputable,
            nombre: String(r['Nombre'] ?? '').trim(),
            nroRubro: Number.isFinite(nroRubro) ? nroRubro : 0,
            imputable: r['Imputable'] === 'Sí' || r['Imputable'] === 'S' ? 'S' : 'N',
            // Exporta etiqueta ("Activo"); API exige código de 1 char ("1").
            tipo: normalizeTipoCuentaCodigo(
              tipoRaw != null && tipoRaw !== '' ? String(tipoRaw) : undefined
            ),
            visibleEn: r['Visible en'] != null ? String(r['Visible en']) : undefined,
            descripcion: r['Descripción'] != null ? String(r['Descripción']) : undefined
          };

          const existente = await imputableService.getImputable(codImputable);
          if (existente) {
            await imputableService.updateImputable(codImputable, imputable as any);
            actualizados += 1;
          } else {
            await imputableService.createImputable(imputable as any);
            creados += 1;
          }
        }

        enqueueSnackbar(
          `Importación OK: ${creados} creados, ${actualizados} actualizados` +
            (omitidos ? `, ${omitidos} omitidos` : ''),
          { variant: 'success' }
        );
        fetchImputables();
      } catch (error: any) {
        console.error('Error al importar:', error);
        const apiMsg =
          error?.response?.data?.errors?.TipoCuentaImputa?.[0] ||
          (typeof error?.response?.data === 'string' ? error.response.data : null) ||
          error?.response?.data?.message ||
          error?.message;
        enqueueSnackbar(
          apiMsg ? `Error al importar: ${apiMsg}` : 'Error al importar datos',
          { variant: 'error' }
        );
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Imputables
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleInformePdfPlanCuentas}
                disabled={exportingPdf}
              >
                {exportingPdf ? 'Generando…' : 'Plan de cuentas'}
              </Button>
              <Button variant="outlined" color="primary" startIcon={<FileDownloadIcon />} onClick={handleExportExcel}>
                Exportar
              </Button>
              <Button variant="outlined" color="primary" component="label" startIcon={<FileUploadIcon />}>
                Importar
                <input type="file" hidden accept=".xlsx,.xls" onChange={handleImportExcel} />
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/contabilidad/imputables/nuevo')}>
                Nuevo Imputable
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {imputables.map((imputable) => (
                <Card key={imputable.codImputable} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {imputable.codImputable} - {imputable.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rubro: {etiquetaRubroNoImputable(imputable.nroRubro)} | Tipo: {tipoCuentaLabel(imputable.tipo)} | Visible en:{' '}
                      {visibleEnReporte(imputable.visibleEn)}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/contabilidad/imputables/${imputable.codImputable}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(imputable.codImputable)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>No imputable (rubro)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Imputable</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo de cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Visible en...</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {imputables.map((imputable) => (
                    <TableRow key={imputable.codImputable} hover>
                      <TableCell>{imputable.codImputable}</TableCell>
                      <TableCell>{imputable.nombre}</TableCell>
                      <TableCell>{etiquetaRubroNoImputable(imputable.nroRubro)}</TableCell>
                      <TableCell>{imputable.imputable === 'S' ? 'Sí' : 'No'}</TableCell>
                      <TableCell>{tipoCuentaLabel(imputable.tipo)}</TableCell>
                      <TableCell>{visibleEnReporte(imputable.visibleEn)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/contabilidad/imputables/${imputable.codImputable}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(imputable.codImputable)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Plan de cuentas"
          documentId="plan-cuentas-preview"
          documentType="recibo"
          downloadFileName="Plan de cuentas Report.pdf"
        />
      ) : null}
    </Box>
  );
};

export default Imputables;
